import { UserEventRole, UserEventMessage, ToolResultProperties } from '.';

type ToolRole = 'tool_use' | 'tool_response';

export const isToolRole = (role: UserEventRole): role is ToolRole =>
  ['tool_use', 'tool_response'].includes(role);

export const getToolUseIdFromMessage = (message: UserEventMessage): string | undefined => {
  const { role, additionalProperties } = message;
  if (!isToolRole(role) || !additionalProperties) {
    return undefined;
  }
  return role === 'tool_response'
    ? (additionalProperties as ToolResultProperties).toolUseId
    : additionalProperties.id;
};

export const getToolMessageElementId = (role: ToolRole, toolUseId: string): string =>
  `${role}_${encodeURIComponent(toolUseId)}`;

export const getPeerToolMessageElementId = (
  role: UserEventRole,
  toolUseId: string
): string | undefined => {
  if (!isToolRole(role)) {
    return undefined;
  }
  const peerRole: ToolRole = role === 'tool_use' ? 'tool_response' : 'tool_use';
  return getToolMessageElementId(peerRole, toolUseId);
};

export type ToolUsePair = {
  hasUse: boolean;
  hasResponse: boolean;
  toolName?: string;
};

export const buildToolPairInfoByToolUseId = (
  conversation: UserEventMessage[]
): Map<string, ToolUsePair> => {
  const map: Map<string, ToolUsePair> = new Map();

  conversation.forEach((message) => {
    const { role, additionalProperties } = message;
    const toolUseId = getToolUseIdFromMessage(message);
    if (!toolUseId) {
      return;
    }
    const existing = map.get(toolUseId) ?? { hasUse: false, hasResponse: false };
    if (role === 'tool_use') {
      existing.hasUse = true;
      const toolName = additionalProperties?.name;
      if (toolName) {
        existing.toolName = toolName;
      }
      map.set(toolUseId, existing);
      return;
    }
    if (role === 'tool_response') {
      existing.hasResponse = true;
      map.set(toolUseId, existing);
    }
  });

  return map;
};
